import React, { useId, useEffect, useState } from 'react';
import classNames from 'classnames';
import { compose } from '@/utils/story-composer';
import { MarginEnum } from '@/enums/styling';
import { getTailwindClassesByEnum } from '@/utils/cssHelpers';
import InputService from '@/utils/input-service';
import { InputsEnum } from '@/enums/inputs';

function TextInput({
	type = 'text',
	identifier,
	placeholder,
	subtext,
	margin = [],
	required = false,
	error_message: errorMessage,
}) {
	const defaultValue = type === InputsEnum.NUMBER ? '1' : '';
	const [showError, setShowError] = useState(false);
	const [value, setValue] = useState(defaultValue);

	useEffect(() => {
		const inputService = InputService.getInstance();

		inputService.registerInputField(identifier, type, defaultValue, required, isValid => {
			setShowError(!isValid);
		});

		setValue(inputService.getInputValue(identifier) || defaultValue);

		return () => {
			inputService.unregisterInputField(identifier);
		};
	}, []);

	const handleChange = value => {
		const inputService = InputService.getInstance();
		let correctedValue = value;
		if (type === InputsEnum.NUMBER) {
			// No negative numbers
			correctedValue = `${Math.abs(value)}`;
		}

		inputService.storeInputValue(identifier, correctedValue);
		setValue(value);
	};

	const props = {};
	if (type === InputsEnum.NUMBER) {
		props.min = '1';
		props.max = '100';
	}

	const id = useId();
	const tailWindClasses = getTailwindClassesByEnum(MarginEnum, margin);

	return (
		<div className={classNames('w-full', tailWindClasses)}>
			<div className="relative w-full">
				<input
					id={`input_${id}`}
					type={type}
					value={value}
					{...props}
					onChange={e => handleChange(e.target.value)}
					className={classNames(
						`block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-lg border appearance-none focus:outline-none focus:ring-0 peer`,
						showError ? 'border-red-300' : 'border-gray-300',
						showError ? 'focus:border-red-600' : 'focus:border-blue-600'
					)}
					placeholder=" "
				/>
				<label
					htmlFor={`input_${id}`}
					className={classNames(
						`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-blue-600 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 left-1`,
						showError ? 'text-red-500' : 'text-gray-500'
					)}
				>
					{placeholder}
				</label>
			</div>
			{!showError && subtext && <div className="text-xs text-shuttle-grey pl-2 pt-1">{subtext}</div>}
			{!showError && !subtext && <div className="text-xs text-shuttle-grey pl-2 pt-1">&nbsp;</div>}
			{showError && (
				<p id="outlined_error_help" className="mt-2 text-xs text-red-600 dark:text-red-400">
					<span className="font-medium">{errorMessage}</span>
				</p>
			)}
		</div>
	);
}

export default TextInput;
compose('text-input', TextInput);
